import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns-jalali';
import { FaCalendarDay, FaUniversity, FaGlobe, FaThLarge, FaCheckCircle, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import DailyView from './Components/DailyView';
import WeeklyView from './Components/WeeklyView';
import MonthlyView from './Components/MonthlyView';
import api from '../api/api';
import EditTaskModal from '../Home/Components/EditTaskModal';
import AddMeetingModal from '../Home/Components/AddMeetingModal';
import CalendarOwnerFilter from '../components/CalendarOwnerFilter';

const startOfWeek = (date) => {
  const result = new Date(date);
  result.setDate(result.getDate() - ((result.getDay() + 1) % 7));
  result.setHours(0, 0, 0, 0);
  return result;
};

const toCalendarItem = (item, type, calendarOwnerId) => {
  const date = new Date(type === 'meeting' ? item.scheduled_at : item.deadline);
  const endDate = new Date(type === 'meeting' ? (item.end_at || date.getTime() + 60 * 60 * 1000) : date.getTime() + 60 * 60 * 1000);
  const start = date.getHours() + date.getMinutes() / 60;
  const end = endDate.getHours() + endDate.getMinutes() / 60;
  const colors = type === 'meeting'
    ? getMeetingColors(item.type)
    : { backgroundColor: '#ffe4e6', borderColor: '#fb7185', textColor: '#881337' };
  return {
    id: `${type}-${item.id}`,
    title: item.title,
    desc: item.description || '',
    time: date.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
    endTime: endDate.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
    location: type === 'meeting' ? (item.locationorlink || 'بدون مکان') : 'کار محول‌شده',
    category: type === 'meeting' ? item.type : 'کارهای من',
    type,
    date,
    day: Number(format(date, 'd')),
    weekDay: (date.getDay() + 1) % 7,
    start: Math.max(6, start),
    end: end > start ? Math.min(24, end) : Math.min(24, start + 1),
    color: type === 'meeting' ? 'bg-amber-100' : 'bg-rose-100',
    source: item,
    calendarOwnerId: Number(calendarOwnerId),
    canEdit: type === 'meeting' ? item.can_edit === true : undefined,
    backgroundColor: colors.backgroundColor,
    borderColor: colors.borderColor,
    textColor: colors.textColor
  };
};

const isSameDay = (first, second) => first.toDateString() === second.toDateString();

const getMeetingColors = (meetingType) => {
  if (meetingType === 'آنلاین') return { backgroundColor: '#dbeafe', borderColor: '#3b82f6', textColor: '#1e3a8a' };
  if (meetingType === 'حضوری داخل دانشگاه') return { backgroundColor: '#dcfce7', borderColor: '#22c55e', textColor: '#14532d' };
  if (meetingType === 'حضوری خارج دانشگاه') return { backgroundColor: '#ffedd5', borderColor: '#f97316', textColor: '#7c2d12' };
  return { backgroundColor: '#f1f5f9', borderColor: '#64748b', textColor: '#1e293b' };
};

const MyCalendar = () => {
  const { user } = useAuth();
  const [view, setView] = useState('روزانه');
  const [filter, setFilter] = useState('همه');
  const [calendarItems, setCalendarItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchParams] = useSearchParams();
  const [permissions, setPermissions] = useState([]);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isAddMeetingModalOpen, setIsAddMeetingModalOpen] = useState(false);

  useEffect(() => {
    const dateValue = Number(searchParams.get('date'));
    if (dateValue) {
      setSelectedDate(new Date(dateValue));
      setView('روزانه');
    }
  }, [searchParams]);

  useEffect(() => {
    api.get('/calendar-permissions/').then((response) => {
      setPermissions(response.data);
      setSelectedOwnerIds([String(user?.id || '')]);
    }).catch((error) => console.error('Error fetching calendar permissions:', error));
  }, [user?.id]);
  
  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        const [meetingsResponse, tasksResponse] = await Promise.all([
          api.get(`/meetings/?owner_ids=${selectedOwnerIds.join(',')}`),
          api.get(`/tasks/?role=calendar&owner_ids=${selectedOwnerIds.join(',')}`)
        ]);
        const meetings = meetingsResponse.data
          .filter((meeting) => meeting.scheduled_at)
          .map((meeting) => toCalendarItem(meeting, 'meeting', meeting.creator));
        const tasks = tasksResponse.data
          .filter((task) => task.deadline)
          .map((task) => toCalendarItem(task, 'task', task.assignee));
        setCalendarItems([...meetings, ...tasks]);
      } catch (error) {
        console.error('Error fetching calendar data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCalendarData();
  }, [selectedOwnerIds, user?.id]);

  const sharedCalendars = permissions
    .filter((permission) => Number(permission.permitted_user) === Number(user?.id))
    .map((permission) => ({
      id: Number(permission.owner),
      name: permission.owner_name,
      accessLevel: permission.access_level
    }));
  const canEditItem = (item) => {
    if (item.type === 'meeting') {
      const selectedCalendar = sharedCalendars.find(
        (calendar) => Number(calendar.id) === Number(item.calendarOwnerId)
      );
      if (selectedCalendar?.accessLevel === 'View') return false;
      if (item.source?.can_edit !== true || selectedOwnerIds.length !== 1) return false;
      const selectedOwnerId = Number(selectedOwnerIds[0]);
      const selectedCalendarCanEdit = selectedOwnerId === Number(user?.id) || sharedCalendars.some(
        (calendar) => Number(calendar.id) === selectedOwnerId && calendar.accessLevel === 'Edit'
      );
      return selectedCalendarCanEdit;
    }
    const selectedCalendar = sharedCalendars.find(
      (calendar) => Number(calendar.id) === Number(item.calendarOwnerId)
    );
    if (selectedCalendar?.accessLevel === 'View') return false;
    if (Number(item.source?.creator) === Number(user?.id)) return true;
    return item.calendarOwnerId === Number(user?.id) || sharedCalendars.some(
      (calendar) => Number(calendar.id) === item.calendarOwnerId && calendar.accessLevel === 'Edit'
    );
  };
  const editableOwnerIds = [String(user?.id), ...sharedCalendars
    .filter((calendar) => calendar.accessLevel === 'Edit')
    .map((calendar) => String(calendar.id))];
  const canAddToSelectedCalendar = selectedOwnerIds.length === 1
    && editableOwnerIds.includes(selectedOwnerIds[0]);

  const handleAddCalendarMeeting = async (meeting) => {
    if (!canAddToSelectedCalendar) {
      window.alert('برای افزودن جلسه، یک تقویم با دسترسی ویرایش را انتخاب کنید.');
      return;
    }
    try {
      await api.post('/meetings/', {
        ...meeting,
        owner_id: Number(selectedOwnerIds[0])
      });
      setIsAddMeetingModalOpen(false);
      const [meetingsResponse, tasksResponse] = await Promise.all([
        api.get(`/meetings/?owner_ids=${selectedOwnerIds.join(',')}`),
        api.get(`/tasks/?role=calendar&owner_ids=${selectedOwnerIds.join(',')}`)
      ]);
      setCalendarItems([
        ...meetingsResponse.data.filter((item) => item.scheduled_at).map((item) => toCalendarItem(item, 'meeting', item.creator)),
        ...tasksResponse.data.filter((item) => item.deadline).map((item) => toCalendarItem(item, 'task', item.assignee))
      ]);
    } catch (error) {
      console.error('Error creating calendar meeting:', error);
      window.alert('افزودن جلسه انجام نشد. سطح دسترسی این تقویم را بررسی کنید.');
    }
  };

  const handleSaveCalendarItem = async (updatedItem) => {
    if (!canEditItem(updatedItem)) {
      window.alert('این تقویم فقط دسترسی مشاهده دارد.');
      return;
    }
    const endpoint = updatedItem.type === 'meeting' ? `/meetings/${updatedItem.source.id}/` : `/tasks/${updatedItem.source.id}/`;
    const payload = updatedItem.type === 'meeting'
      ? {
          title: updatedItem.source.title,
          description: updatedItem.source.description,
          scheduled_at: updatedItem.source.scheduled_at,
          end_at: updatedItem.source.end_at,
          type: updatedItem.source.type,
          locationorlink: updatedItem.source.locationorlink,
          is_mandatory: updatedItem.source.is_mandatory,
          participants_ids: updatedItem.source.participants || []
        }
      : {
          title: updatedItem.source.title,
          description: updatedItem.source.description,
          status: updatedItem.source.status,
          priority: updatedItem.source.priority,
          deadline: updatedItem.source.deadline
        };
    try {
      await api.patch(endpoint, payload);
      setEditingItem(null);
      const [meetingsResponse, tasksResponse] = await Promise.all([
        api.get(`/meetings/?owner_ids=${selectedOwnerIds.join(',')}`),
        api.get(`/tasks/?role=calendar&owner_ids=${selectedOwnerIds.join(',')}`)
      ]);
      setCalendarItems([
        ...meetingsResponse.data.filter((item) => item.scheduled_at).map((item) => toCalendarItem(item, 'meeting', item.creator)),
        ...tasksResponse.data.filter((item) => item.deadline).map((item) => toCalendarItem(item, 'task', item.assignee))
      ]);
    } catch (error) {
      console.error('Error saving calendar item:', error);
      const detail = error.response?.data
        ? Object.values(error.response.data).flat().join(' ')
        : 'ارتباط با سرور برقرار نشد.';
      window.alert(`ذخیره تغییرات انجام نشد: ${detail}`);
    }
  };
  
  const weekStart = startOfWeek(selectedDate);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);
  const matchesFilter = (item, selectedFilter) => {
    if (selectedFilter === 'جلسات') return item.type === 'meeting';
    if (selectedFilter === 'کارهای من') return item.type === 'task';
    if (selectedFilter === 'داخل دانشگاه') return item.category === 'حضوری داخل دانشگاه';
    if (selectedFilter === 'خارجی') return item.category === 'حضوری خارج دانشگاه';
    if (selectedFilter === 'آنلاین') return item.category === 'آنلاین';
    return true;
  };
  const filteredItems = calendarItems.filter((item) => {
    return matchesFilter(item, filter);
  });

  const moveDate = (direction) => {
    const nextDate = new Date(selectedDate);
    if (view === 'روزانه') nextDate.setDate(nextDate.getDate() + direction);
    if (view === 'هفتگی') nextDate.setDate(nextDate.getDate() + (direction * 7));
    if (view === 'ماهانه') nextDate.setMonth(nextDate.getMonth() + direction);
    setSelectedDate(nextDate);
  };

  const resetDate = () => setSelectedDate(new Date());
  const visibleItems = filteredItems.filter((item) => {
    if (view === 'روزانه') return isSameDay(item.date, selectedDate);
    if (view === 'هفتگی') return item.date >= weekStart && item.date < weekEnd;
    return format(item.date, 'yyyy/MM') === format(selectedDate, 'yyyy/MM');
  });
  
  const filters = [
    { value: 'جلسات', label: 'جلسات', icon: FaCalendarDay, color: 'text-amber-700', backgroundColor: '#fef3c7', borderColor: '#f59e0b' },
    { value: 'داخل دانشگاه', label: 'دانشگاه', icon: FaUniversity, color: 'text-emerald-700', backgroundColor: '#dcfce7', borderColor: '#22c55e' },
    { value: 'خارجی', label: 'خارجی', icon: FaGlobe, color: 'text-orange-700', backgroundColor: '#ffedd5', borderColor: '#f97316' },
    { value: 'آنلاین', label: 'آنلاین', icon: FaCalendarDay, color: 'text-blue-700', backgroundColor: '#dbeafe', borderColor: '#3b82f6' },
    { value: 'کارهای من', label: 'کارهای من', icon: FaCheckCircle, color: 'text-rose-700', backgroundColor: '#ffe4e6', borderColor: '#fb7185' },
    { value: 'همه', label: 'همه', icon: FaThLarge, color: 'text-slate-700', backgroundColor: '#f1f5f9', borderColor: '#94a3b8' }
  ];
  
  if (loading) return <div className="p-8 text-center">در حال بارگذاری تقویم...</div>;
  
  return (
    <div className="bg-[#f8fafc] min-h-screen text-slate-800" dir="rtl">
      <div className="flex min-h-screen">
        <main className="flex-1 min-w-0 p-4 md:p-6">
          <div className="max-w-[1050px] mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1">
                  {['روزانه', 'هفتگی', 'ماهانه'].map((itemView) => (
                    <button key={itemView} onClick={() => setView(itemView)} className={`px-4 py-2 rounded-lg text-xs font-bold ${view === itemView ? 'bg-indigo-100 text-indigo-700' : 'text-slate-400'}`}>
                      {itemView}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-slate-400">{selectedDate.toLocaleDateString('fa-IR')}</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => moveDate(1)} className="p-2 text-slate-500 hover:text-indigo-600" aria-label="بازه بعدی"><FaChevronRight /></button>
                  <button type="button" onClick={resetDate} className="px-2 py-1 text-[10px] text-indigo-600 font-bold">امروز</button>
                  <button type="button" onClick={() => moveDate(-1)} className="p-2 text-slate-500 hover:text-indigo-600" aria-label="بازه قبلی"><FaChevronLeft /></button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CalendarOwnerFilter selectedIds={selectedOwnerIds} onChange={setSelectedOwnerIds} />
                {canAddToSelectedCalendar && (
                  <button
                    type="button"
                    onClick={() => setIsAddMeetingModalOpen(true)}
                    className="bg-indigo-600 text-white px-5 py-2 rounded-xl text-xs font-bold shadow-md"
                  >
                    + افزودن
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
              {filters.map(({ value, label, icon: Icon, color, backgroundColor, borderColor }) => {
                const count = calendarItems.filter((item) => matchesFilter(item, value)).length;
                return (
                  <button key={value} onClick={() => setFilter(value)} style={{ backgroundColor, borderColor }} className={`min-h-[76px] rounded-2xl border-2 text-right p-3 transition-transform hover:scale-[1.02] ${filter === value ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}>
                    <div className="flex justify-between items-start"><Icon className={color} /><span className="text-[10px] text-slate-600">{count}</span></div>
                    <span className="block mt-3 text-xs font-bold">{label}</span>
                  </button>
                );
              })}
            </div>
  
            {view === 'روزانه' && <DailyView meetings={visibleItems} onItemClick={setEditingItem} />}
            {view === 'هفتگی' && <WeeklyView meetings={visibleItems} weekStart={weekStart} onItemClick={setEditingItem} />}
            {view === 'ماهانه' && <MonthlyView meetings={visibleItems} currentDate={selectedDate} onItemClick={setEditingItem} />}
            {visibleItems.length === 0 && <p className="mt-4 text-center text-sm text-slate-400">برای این فیلتر و بازه موردی وجود ندارد.</p>}
          </div>
        </main>
  
      </div>
      {isAddMeetingModalOpen && <AddMeetingModal isOpen onClose={() => setIsAddMeetingModalOpen(false)} onAdd={handleAddCalendarMeeting} />}
      {editingItem?.type === 'task' && <EditTaskModal isOpen task={editingItem.source} readOnly={!canEditItem(editingItem)} onClose={() => setEditingItem(null)} onSave={(task) => handleSaveCalendarItem({ ...editingItem, source: task })} />}
      {editingItem?.type === 'meeting' && <AddMeetingModal isOpen initialData={editingItem.source} readOnly={!canEditItem(editingItem)} onClose={() => setEditingItem(null)} onAdd={(meeting) => handleSaveCalendarItem({ ...editingItem, source: { ...editingItem.source, ...meeting } })} />}
    </div>
  );
};
  
export default MyCalendar;